import * as THREE from 'three';
import { Reflector } from 'three/examples/jsm/objects/Reflector.js';

/**
 * Port of nhtoby311/WaterSurface `WaterComplex` (itself based on three.js `Water2`)
 * to plain Three.js. Two scrolling normal maps distort a real-time planar
 * Reflection/Refraction; an interactive FX texture (`u_fx`, fed by the ripple/fluid
 * sim) further distorts the reflection and tints the surface where the pointer moves.
 *
 * Original (React) defaults are preserved on the call site (see `WaterFX`).
 *
 * DESKTOP-ONLY (2026-09-04): low-end devices (phones, software WebGL, LOW boot tier) never
 * construct this class at all — `WaterFX` serves them the standalone `WaterSimple` instead,
 * so none of the Reflector/nested-render machinery here even exists there.
 */
export interface WaterComplexOptions {
  color?: THREE.ColorRepresentation;
  textureWidth?: number;
  textureHeight?: number;
  clipBias?: number;
  flowDirection?: THREE.Vector2;
  flowSpeed?: number;
  reflectivity?: number;
  scale?: number;
  flowMap?: THREE.Texture;
  normalMap0?: THREE.Texture;
  normalMap1?: THREE.Texture;
  fxDistortionFactor?: number;
  fxDisplayColorAlpha?: number;
}

type Uniforms = Record<string, THREE.IUniform>;

export class WaterComplex extends THREE.Mesh {
  static readonly WaterShader = {
    uniforms: {
      color: { value: null },
      deepColor: { value: new THREE.Color(0x20323a) },
      reflectivity: { value: 0 },
      distortionScale: { value: 0.05 },
      uSunDir: { value: new THREE.Vector3(-0.3, 0.6, -0.4) },
      uSunColor: { value: new THREE.Color(0xfff4dc) },
      uSunShininess: { value: 260 },
      uSunIntensity: { value: 0.6 },
      tReflectionMap: { value: null },
      tRefractionMap: { value: null },
      /** 1 = sample the live planar reflection; 0 = the low-tier fallback sky tint. */
      uReflectionMix: { value: 1 },
      uFallbackColor: { value: new THREE.Color(0xcfdde8) },
      tNormalMap0: { value: null },
      tNormalMap1: { value: null },
      textureMatrix: { value: null },
      config: { value: new THREE.Vector4() },
      u_fx: { value: null },
      fxDistortionFactor: { value: 0.0 },
      fxDisplayColorAlpha: { value: 1.0 },
    },
    vertexShader: /* glsl */ `
      #include <common>
      #include <fog_pars_vertex>
      #include <logdepthbuf_pars_vertex>

      uniform mat4 textureMatrix;

      varying vec4 vCoord;
      varying vec2 vUv;
      varying vec3 vToEye;

      void main() {
        vUv = uv;
        vCoord = textureMatrix * vec4( position, 1.0 );

        vec4 worldPosition = modelMatrix * vec4( position, 1.0 );
        vToEye = cameraPosition - worldPosition.xyz;

        vec4 mvPosition = viewMatrix * worldPosition;
        gl_Position = projectionMatrix * mvPosition;

        #include <logdepthbuf_vertex>
        #include <fog_vertex>
      }`,
    fragmentShader: /* glsl */ `
      #include <common>
      #include <fog_pars_fragment>
      #include <logdepthbuf_pars_fragment>

      uniform sampler2D tReflectionMap;
      uniform sampler2D tRefractionMap;
      uniform sampler2D tNormalMap0;
      uniform sampler2D tNormalMap1;

      #ifdef USE_FLOWMAP
        uniform sampler2D tFlowMap;
      #else
        uniform vec2 flowDirection;
      #endif

      uniform vec3 color;
      uniform vec3 deepColor;
      uniform float uReflectionMix;
      uniform vec3 uFallbackColor;
      uniform float reflectivity;
      uniform float distortionScale;
      uniform vec3 uSunDir;
      uniform vec3 uSunColor;
      uniform float uSunShininess;
      uniform float uSunIntensity;
      uniform vec4 config;

      uniform sampler2D u_fx;
      uniform float fxDistortionFactor;
      uniform float fxDisplayColorAlpha;

      varying vec4 vCoord;
      varying vec2 vUv;
      varying vec3 vToEye;

      void main() {
        #include <logdepthbuf_fragment>

        float flowMapOffset0 = config.x;
        float flowMapOffset1 = config.y;
        float halfCycle = config.z;
        float scale = config.w;

        vec3 toEye = normalize( vToEye );

        vec2 flow;
        #ifdef USE_FLOWMAP
          flow = texture2D( tFlowMap, vUv ).rg * 2.0 - 1.0;
        #else
          flow = flowDirection;
        #endif
        flow.x *= - 1.0;

        vec4 normalColor0 = texture2D( tNormalMap0, ( vUv * scale ) + flow * flowMapOffset0 );
        vec4 normalColor1 = texture2D( tNormalMap1, ( vUv * scale ) + flow * flowMapOffset1 );

        float flowLerp = abs( halfCycle - flowMapOffset0 ) / halfCycle;
        vec4 normalColor = mix( normalColor0, normalColor1, flowLerp );

        vec3 normal = normalize( vec3( normalColor.r * 2.0 - 1.0, normalColor.b, normalColor.g * 2.0 - 1.0 ) );

        float theta = max( dot( toEye, normal ), 0.0 );
        // Schlick Fresnel: F0 (from IOR) head-on, ramping to a near-mirror at grazing angles.
        float reflectance = clamp( reflectivity + ( 1.0 - reflectivity ) * pow( ( 1.0 - theta ), 5.0 ), 0.0, 1.0 );

        vec3 coord = vCoord.xyz / vCoord.w;
        vec2 uv = coord.xy + coord.z * normal.xz * distortionScale;

        // The ripple/fluid sim's dye values are display-intensity HDR (can run well past 1.0,
        // especially the fluid splat colour), but here they double as a UV offset into the
        // reflection map — left unclamped, a single splat blows the lookup wildly out of the
        // pool's texture space, sampling clamped edge texels as solid, wrong-coloured blotches
        // (looked like brown "map continents" smeared across the water). Clamp both uses so the
        // sim can run as hot as it wants without ever corrupting the reflection sample.
        vec4 fx = texture2D( u_fx, vUv );
        vec3 fxSafe = clamp( fx.rgb, 0.0, 1.0 );
        float avgDistortion = clamp( ( fxSafe.r + fxSafe.g + fxSafe.b ) / 3.0 * fxDistortionFactor, -0.08, 0.08 );

        vec4 reflectColor = texture2D( tReflectionMap, vec2( 1.0 - uv.x, uv.y ) + avgDistortion );
        vec4 refractColor = texture2D( tRefractionMap, uv );
        // LOW-TIER FALLBACK (uReflectionMix = 0): no live reflection exists, so stand in a
        // soft sky tint gently modulated by the ripple normals — the water stays alive
        // (Fresnel, sun sparkle, flow all still apply) without any nested scene render.
        vec3 fallback = uFallbackColor * ( 0.9 + 0.22 * normal.y + 0.18 * normal.x * normal.z );
        reflectColor.rgb = mix( fallback, reflectColor.rgb, uReflectionMix );
        refractColor.rgb = mix( fallback, refractColor.rgb, uReflectionMix );

        // Realistic body: blend the deep-water colour (head-on) toward the tinted
        // reflection (grazing) by the Fresnel term — the old shader computed reflectance
        // then ignored it, always showing a flat tinted mirror.
        vec3 reflected = color * reflectColor.rgb;
        vec3 surface = mix( deepColor, reflected, reflectance );

        // Sharp sun/sky glint (Blinn-Phong against a fixed light dir — the scene sun is off).
        vec3 sunDir = normalize( uSunDir );
        vec3 halfV = normalize( sunDir + toEye );
        float spec = pow( max( dot( normal, halfV ), 0.0 ), uSunShininess ) * uSunIntensity;
        surface += uSunColor * spec;

        float luminance = clamp( dot( fxSafe * fxDisplayColorAlpha, vec3( 0.299, 0.587, 0.114 ) ), 0.0, 1.0 );
        vec3 mixedColor = mix( surface, fxSafe * fxDisplayColorAlpha, luminance * fxDisplayColorAlpha );

        gl_FragColor = vec4( mixedColor, 1.0 );

        #include <tonemapping_fragment>
        #include <colorspace_fragment>
        #include <fog_fragment>
      }`,
  };

  declare material: THREE.ShaderMaterial;
  /** LAZY (2026-09-01, low-end pass): created only when live reflections are actually wanted.
   *  A device that boots straight into the LOW tier (software/CPU WebGL) runs in the fallback
   *  look from its very first frame, so it never pays for the Reflector's render target or its
   *  nested-render machinery at all — the water can't fail to appear there, because there is
   *  nothing left to fail. See {@link ensureReflector}. */
  private reflector: Reflector | null = null;
  /** Desired reflection RT size — applied when the Reflector is (eventually) created. */
  private reflectionRes: number;
  private readonly clipBias: number;
  /** Called with true/false around this pool's nested reflection render — see the note at the
   *  call site. Wired by `World.setupWater`. */
  onReflectionRender: ((active: boolean) => void) | null = null;
  private readonly clock = new THREE.Clock();
  private readonly cycle = 0.15;
  private readonly halfCycle = this.cycle * 0.5;
  /** Public so the debug GUI can tune flow speed live. */
  flowSpeed: number;
  /**
   * When false the planar reflection is NOT re-rendered every frame — the surface keeps its
   * last reflection texture (a frozen mirror) and only the ripple normals keep animating. This
   * is the single biggest perf lever in the scene: each in-view pool otherwise re-renders the
   * ENTIRE scene every frame, so with 3 pools that's up to 3 extra full renders. Used by the
   * `low` quality profile for weak / software-WebGL devices.
   *
   * v2 of the low-tier story (2026-07-23, the sister's-laptop bug): a hard freeze rendered the
   * pool BLACK on devices that boot straight into the low tier (the "last texture" had never
   * been rendered), and a mid-scroll refresh variant made the water flicker + spiked frames.
   * Low tier now uses {@link setLiveReflections}(false): NO reflection rendering at all — the
   * shader swaps the reflection sample for a soft fallback sky tint (uReflectionMix), keeping
   * the ripples, Fresnel and sun sparkle. Stable, never black, zero nested renders.
   */
  reflectionsEnabled = true;
  private readonly textureMatrix = new THREE.Matrix4();
  /** Sibling `WaterComplex` meshes, hidden alongside `this` during its own reflection
   *  pass (see `onBeforeRender` + `setPeers` below). */
  private peers: THREE.Object3D[] = [];

  constructor(geometry: THREE.BufferGeometry, options: WaterComplexOptions = {}) {
    super(geometry);

    const color = options.color !== undefined ? new THREE.Color(options.color) : new THREE.Color(0xffffff);
    const textureWidth = options.textureWidth || 512;
    const textureHeight = options.textureHeight || 512;
    const clipBias = options.clipBias || 0;
    const flowDirection = options.flowDirection || new THREE.Vector2(1, 0);
    this.flowSpeed = options.flowSpeed || 0.03;
    const reflectivity = options.reflectivity || 0.02;
    const scale = options.scale || 1;
    const shader = WaterComplex.WaterShader;

    const flowMap = options.flowMap;
    const normalMap0 = options.normalMap0;
    const normalMap1 = options.normalMap1;

    this.reflectionRes = Math.max(textureWidth, textureHeight);
    this.clipBias = clipBias;

    this.material = new THREE.ShaderMaterial({
      uniforms: THREE.UniformsUtils.merge([THREE.UniformsLib['fog'], shader.uniforms]),
      vertexShader: shader.vertexShader,
      fragmentShader: shader.fragmentShader,
      transparent: false,
      fog: true,
    });
    const uniforms = this.material.uniforms as Uniforms;

    if (flowMap !== undefined) {
      this.material.defines.USE_FLOWMAP = '';
      uniforms['tFlowMap'] = { value: flowMap };
    } else {
      uniforms['flowDirection'] = { value: flowDirection };
    }

    if (normalMap0) normalMap0.wrapS = normalMap0.wrapT = THREE.RepeatWrapping;
    if (normalMap1) normalMap1.wrapS = normalMap1.wrapT = THREE.RepeatWrapping;

    // The reflection samplers stay null until `ensureReflector` runs (they're only ever read
    // while `uReflectionMix` is 1, and turning that on is what creates the Reflector).
    uniforms['tNormalMap0']!.value = normalMap0 ?? null;
    uniforms['tNormalMap1']!.value = normalMap1 ?? null;

    uniforms['color']!.value = color;
    uniforms['reflectivity']!.value = reflectivity;
    uniforms['textureMatrix']!.value = this.textureMatrix;

    uniforms['u_fx']!.value = null;
    uniforms['fxDistortionFactor']!.value = options.fxDistortionFactor ?? 0.0;
    uniforms['fxDisplayColorAlpha']!.value = options.fxDisplayColorAlpha ?? 1.0;

    const config = uniforms['config']!.value as THREE.Vector4;
    config.x = 0; // flowMapOffset0
    config.y = this.halfCycle; // flowMapOffset1
    config.z = this.halfCycle; // halfCycle
    config.w = scale; // scale

    type BeforeRender = (renderer: THREE.WebGLRenderer, scene: THREE.Scene, camera: THREE.Camera) => void;
    this.onBeforeRender = (renderer, scene, camera) => {
      this.updateTextureMatrix(camera);
      this.updateFlow();

      // Fallback mode (low quality tier): the shader isn't sampling the reflection at all
      // (uReflectionMix = 0), so skip the whole nested scene re-render.
      if (!this.reflectionsEnabled) return;

      const reflector = this.ensureReflector();

      // Hide this AND every sibling `WaterComplex` (see `setPeers`) for the duration of
      // this instance's own reflection pass. `Reflector.onBeforeRender` renders the WHOLE
      // scene into its render target — with 2+ reflective planes present and only THIS
      // one hidden, every OTHER still-visible pool would fire ITS OWN `onBeforeRender`
      // during that nested render, recursively re-entering this same code. Hiding every
      // water mesh (not just self) means no OTHER water's `onBeforeRender` can fire
      // mid-pass — each pool just stops reflecting the others (not visually missed, since
      // pools this far apart were never in each other's reflection anyway) and, more
      // importantly, this avoids the wasted GPU cost of that nested recursive rendering —
      // which mattered concretely: with all 3 pools simultaneously in view + unhidden,
      // the extra render load was enough to trip the adaptive-quality monitor mid-scroll,
      // which in turn hit a SEPARATE real bug (see `PostProcessing.resize`'s comment) that
      // threw a genuine WebGL "Feedback loop formed between Framebuffer and active
      // Texture" error (2026-07-05).
      const group: THREE.Object3D[] = [this, ...this.peers];
      const prevVisible = group.map((o) => o.visible);
      for (const o of group) o.visible = false;

      // Nested-pass hook (e.g. the cloud sea bypasses its screen-space soft fade, which samples
      // the MAIN camera's depth and would mangle the reflected clouds). try/finally so a throw
      // inside the nested render can never leave the hook stuck on or the pools hidden.
      this.onReflectionRender?.(true);
      try {
        reflector.matrixWorld.copy(this.matrixWorld);
        (reflector.onBeforeRender as unknown as BeforeRender)(renderer, scene, camera);
      } finally {
        this.onReflectionRender?.(false);
        group.forEach((o, i) => {
          o.visible = prevVisible[i]!;
        });
      }
    };
  }

  /** Register sibling `WaterComplex` meshes so this instance's reflection pass hides
   *  them too (see `onBeforeRender` above). Call once after all water meshes exist. */
  setPeers(peers: THREE.Object3D[]): void {
    this.peers = peers;
  }

  /** Drive the interactive FX distortion texture (ripple/fluid output). */
  setFxTexture(texture: THREE.Texture | null): void {
    (this.material.uniforms as Uniforms)['u_fx']!.value = texture;
  }

  /** Resize the planar reflection render target (adaptive quality). Remembered even before the
   *  Reflector exists, so a later tier climb creates it at the right size. */
  setReflectionResolution(px: number): void {
    this.reflectionRes = px;
    this.reflector?.getRenderTarget().setSize(px, px);
  }

  /** LIVE planar reflections on/off. Off (the low tier) = zero nested scene renders AND the
   *  shader stops sampling the (possibly never-created) reflection target entirely — the
   *  fallback sky tint stands in. See the v2 note on {@link reflectionsEnabled}. */
  setLiveReflections(on: boolean): void {
    this.reflectionsEnabled = on;
    if (on) this.ensureReflector();
    (this.material.uniforms['uReflectionMix'] as THREE.IUniform<number>).value = on ? 1 : 0;
  }

  /** Build the Reflector on first actual use (see the note on {@link reflector}). */
  private ensureReflector(): Reflector {
    if (this.reflector) return this.reflector;
    const reflector = new Reflector(this.geometry, {
      textureWidth: this.reflectionRes,
      textureHeight: this.reflectionRes,
      clipBias: this.clipBias,
    });
    reflector.matrixAutoUpdate = false;
    // The shader's final colour uses only the reflection + FX (refraction is unused),
    // so we skip the Refractor entirely and point its sampler at the reflection map.
    const uniforms = this.material.uniforms as Uniforms;
    uniforms['tReflectionMap']!.value = reflector.getRenderTarget().texture;
    uniforms['tRefractionMap']!.value = reflector.getRenderTarget().texture;
    this.reflector = reflector;
    return reflector;
  }

  private updateTextureMatrix(camera: THREE.Camera): void {
    this.textureMatrix.set(
      0.5, 0.0, 0.0, 0.5,
      0.0, 0.5, 0.0, 0.5,
      0.0, 0.0, 0.5, 0.5,
      0.0, 0.0, 0.0, 1.0,
    );
    this.textureMatrix.multiply(camera.projectionMatrix);
    this.textureMatrix.multiply(camera.matrixWorldInverse);
    this.textureMatrix.multiply(this.matrixWorld);
  }

  private updateFlow(): void {
    const delta = this.clock.getDelta();
    const config = (this.material.uniforms as Uniforms)['config']!.value as THREE.Vector4;
    config.x += this.flowSpeed * delta;
    config.y = config.x + this.halfCycle;
    if (config.x >= this.cycle) {
      config.x = 0;
      config.y = this.halfCycle;
    } else if (config.y >= this.cycle) {
      config.y = config.y - this.cycle;
    }
  }

  dispose(): void {
    this.geometry.dispose();
    this.material.dispose();
    (this.reflector?.getRenderTarget() as THREE.WebGLRenderTarget | undefined)?.dispose();
  }
}
